<template>
  <div>
    <GameAlert
      :show="!!alertConfig.message"
      :type="alertConfig.type"
      :message="alertConfig.message"
      @close="closeAlert"
    />

    <Loading
      v-if="creatingPlayer"
      :message="$t('login.preparingAdventure')"
      :show-confirm="playerCreated"
      :confirm-message="$t('login.characterReady')"
      @continue="goToHome"
    />

    <!--
      A PORTA DO JOGO (redesign 03/09). A versão antiga era um cartão azul com blur sobre um
      OceanBackground de CSS — a única tela que não era do jogo. Agora é o MESMO mar das telas de
      dentro (FishScene), e o formulário é a ficha de papel do porto: entrar no Fishbay é
      preencher a ficha do cais. Os ids (#email, #password) e a classe .submit-btn ficam — os
      testes dirigidos entram por eles.
    -->
    <FishScene v-else cena="mar-aberto" :escurecimento="0.35" class="porta">
      <div class="porta__interior">
        <div class="porta__marca">
          <img class="porta__logo suavizada" src="/logotipos/logo-fishbay.png" alt="" />
          <h1 class="porta__titulo t-marca">
            <span
              v-for="(char, index) in titleChars"
              :key="index"
              class="porta__letra"
              :style="{ animationDelay: `${index * 0.09}s` }"
            >{{ char }}</span>
          </h1>
        </div>

        <div class="porta__ficha">
          <!-- As duas fitas no alto da ficha: entrar ou registrar. -->
          <div class="porta__fitas" role="tablist">
            <button
              type="button"
              role="tab"
              :aria-selected="!isRegister"
              :class="['porta__fita', { 'porta__fita--ativa': !isRegister }]"
              @click="isRegister = false"
            >
              {{ $t('login.login') }}
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="isRegister"
              :class="['porta__fita', { 'porta__fita--ativa': isRegister }]"
              @click="isRegister = true"
            >
              {{ $t('login.register') }}
            </button>
          </div>

          <div class="porta__papel">
            <p v-if="error" class="porta__erro">{{ error }}</p>

            <form @submit.prevent="handleSubmit" class="porta__form">
              <label class="porta__campo" v-if="isRegister">
                <span>{{ $t('login.username') }}</span>
                <input
                  type="text"
                  id="username"
                  v-model="formData.username"
                  autocomplete="username"
                  required
                >
              </label>

              <!--
                No cadastro o campo é o e-mail e o navegador confere o formato. No login ele
                aceita **e-mail ou nick**, que é o que `POST /api/auth/login` sempre aceitou em
                `identifier`: com `type="email"` o navegador barrava o envio de quem entrava
                pelo nome de usuário, e o jogador via o formulário simplesmente não responder.
              -->
              <label class="porta__campo">
                <span>{{ isRegister ? $t('login.email') : $t('login.identifier') }}</span>
                <input
                  :type="isRegister ? 'email' : 'text'"
                  id="email"
                  v-model="formData.email"
                  :autocomplete="isRegister ? 'email' : 'username'"
                  required
                >
              </label>

              <label class="porta__campo">
                <span>{{ $t('login.password') }}</span>
                <span class="porta__senha">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="formData.password"
                    :autocomplete="isRegister ? 'new-password' : 'current-password'"
                    :minlength="isRegister ? minimoDaSenha : null"
                    required
                  >
                  <button
                    type="button"
                    class="porta__olho alvo-invisivel"
                    :aria-label="showPassword ? 'Esconder senha' : 'Mostrar senha'"
                    @click="showPassword = !showPassword"
                  >
                    <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                </span>
                <!--
                  A regra dos 12 caracteres é do servidor (`contracts/auth.ts`) e não estava
                  escrita em lugar nenhum da tela: o jogador digitava a senha de sempre e lia
                  "Dados invalidos", sem saber o que corrigir — e cada tentativa dessas
                  consumia o limite de 5 cadastros por hora do IP dele.
                -->
                <small class="porta__aviso" v-if="isRegister">{{ $t('login.passwordHint') }}</small>
              </label>

              <label class="porta__campo" v-if="isRegister">
                <span>{{ $t('login.confirmPassword') }}</span>
                <input
                  type="password"
                  id="confirmPassword"
                  v-model="formData.confirmPassword"
                  autocomplete="new-password"
                  required
                >
                <small class="porta__aviso" v-if="passwordError">{{ passwordError }}</small>
              </label>

              <div class="porta__acoes">
                <FishButton
                  class="submit-btn"
                  tipo="submit"
                  variante="primario"
                  tamanho="grande"
                  bloco
                  :desabilitado="isRegister && (!passwordsMatch || !passwordLongEnough)"
                >
                  {{ isRegister ? $t('login.createAccount') : $t('login.enter') }}
                </FishButton>
                <button
                  v-if="!isRegister"
                  type="button"
                  class="porta__esqueci"
                  @click="handleForgotPassword"
                >
                  {{ $t('login.forgotPassword') }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- A comunidade, discreta: três âncoras de texto no rodapé da tela, não botões-bolha. -->
        <nav class="porta__comunidade" :aria-label="$t('login.social.joinCommunity')">
          <a href="https://www.linkedin.com/company/101185636" target="_blank" rel="noopener">LinkedIn</a>
          <a href="https://www.instagram.com/omundodealanreis/" target="_blank" rel="noopener">Instagram</a>
          <a href="https://t.me/+byBMdvSfpEVmMTdh" target="_blank" rel="noopener">Telegram</a>
        </nav>
      </div>
    </FishScene>
  </div>
</template>

<script>
import Loading from '../shared/common/Loading.vue';
import GameAlert from '../shared/common/Alert.vue';
import { FishScene, FishButton } from '@/shared/ui';
import { temSessao } from '@/core/services/api/v2/session';

/**
 * O mínimo que `registerSchema` exige (`packages/contracts/src/auth.ts`).
 *
 * Vive em dois lugares porque o cliente precisa dizer a regra **antes** de
 * gastar uma tentativa; o servidor continua sendo quem decide. Se o número
 * mudar lá, muda aqui — é o mesmo pacto de `luck` entre domínio e tela.
 */
const MINIMO_DA_SENHA = 12;

/**
 * A mensagem que o jogador lê quando o servidor recusa.
 *
 * O V2 responde `{ message, erros: [{ campo, motivo }] }` na falha de
 * validação, e a tela só lia `message` — que nesse caso é "Dados invalidos",
 * sem dizer qual campo nem por quê. O motivo real ficava no array, invisível.
 */
function mensagemDoErro(error, traduzir) {
  const corpo = error.response?.data;

  if (error.response?.status === 429) return traduzir('login.tooManyAttempts');

  const erros = Array.isArray(corpo?.erros) ? corpo.erros : [];
  if (erros.length > 0) {
    return erros.map((e) => e.motivo).filter(Boolean).join('. ');
  }

  // O Strapi aninhava em `error.message`; o V2 é plano. Os dois seguem lidos
  // porque nem tudo que fala com esta tela migrou.
  return corpo?.message || corpo?.error?.message || traduzir('login.error');
}

export default {
  name: 'LoginPage',
  components: {
    Loading,
    GameAlert,
    FishScene,
    FishButton
  },
  data() {
    return {
      isRegister: false,
      formData: {
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
      },
      error: null,
      loading: false,
      creatingPlayer: false,
      playerCreated: false,
      loadingMessage: 'Preparando sua aventura...',
      checkInterval: null,
      referralId: null,
      showPassword: false,
      /** Exposto ao template para o `minlength` do campo de senha. */
      minimoDaSenha: MINIMO_DA_SENHA,
      alertConfig: {
        message: '',
        type: 'info'
      }
    }
  },
  computed: {
    titleChars() {
      return this.$t('login.title').split('');
    },
    passwordsMatch() {
      if (!this.isRegister) return true;
      return this.formData.password === this.formData.confirmPassword;
    },
    /**
     * A senha atende ao mínimo do servidor?
     *
     * Conferir aqui não é duplicar a validação por gosto: a tentativa que o
     * servidor recusa com 400 **consome o balde do rate limit** (5 cadastros
     * por hora, por IP), então quem errasse o tamanho cinco vezes ficava uma
     * hora trancado do lado de fora já com a senha certa.
     */
    passwordLongEnough() {
      if (!this.isRegister) return true;
      return this.formData.password.length >= MINIMO_DA_SENHA;
    },
    passwordError() {
      if (!this.isRegister) return '';
      if (this.formData.password && !this.passwordLongEnough) {
        return this.$t('login.passwordTooShort');
      }
      if (this.formData.confirmPassword && !this.passwordsMatch) {
        return this.$t('login.passwordMismatch');
      }
      return '';
    }
  },
  methods: {
    async handleSubmit() {
      // Barra antes de sair da máquina: a recusa do servidor custaria uma das
      // cinco tentativas por hora que o IP tem.
      if (this.isRegister && !this.passwordLongEnough) {
        this.error = this.$t('login.passwordTooShort');
        this.$oraculoLogs.warn('Password too short on registration', {
          details: {
            email: this.formData.email,
            username: this.formData.username,
            tamanho: this.formData.password.length
          }
        });
        return;
      }

      if (this.isRegister && !this.passwordsMatch) {
        // Log de validação de senha
        this.$oraculoLogs.warn('Password mismatch on registration', {
          details: {
            email: this.formData.email,
            username: this.formData.username
          }
        });
        return;
      }

      this.error = null;

      try {
        if (this.isRegister) {
          this.creatingPlayer = true; // Ativar loading no início do registro

          const referralId = sessionStorage.getItem('referralId');

          // Log de início de registro
          this.$oraculoLogs.info('Registration attempt started', {
            details: {
              email: this.formData.email,
              username: this.formData.username,
              hasReferral: !!referralId
            }
          });

          await this.$store.dispatch('auth/register', {
            username: this.formData.username,
            email: this.formData.email,
            password: this.formData.password,
            referralId
          });

          this.startPlayerCheck();
        } else {
          // Log de início de login
          this.$oraculoLogs.info('Login attempt started', {
            details: {
              email: this.formData.email
            }
          });

          await this.$store.dispatch('auth/login', {
            email: this.formData.email,
            password: this.formData.password
          });
          this.$router.push('/home');
        }
      } catch (error) {
        console.error('Auth error:', error);
        this.error = mensagemDoErro(error, this.$t.bind(this));

        // Log detalhado de erro de autenticação
        this.$oraculoLogs.error(
          this.isRegister ? 'Registration failed' : 'Login failed',
          {
            details: {
              email: this.formData.email,
              username: this.isRegister ? this.formData.username : undefined,
              errorMessage: this.error,
              statusCode: error.response?.status
            },
            http: {
              method: 'POST',
              path: this.isRegister ? '/api/auth/register' : '/api/auth/login',
              status: error.response?.status || 500
            },
            error: this.$oraculoLogs.captureErrorDetails(error)
          }
        );

        this.creatingPlayer = false; // Só desativa loading em caso de erro
      }
    },

    /**
     * Confirma que o personagem existe depois do cadastro.
     *
     * Era um `setInterval` de dois segundos, e fazia sentido no Strapi: lá a
     * conta e o jogador eram duas escritas soltas, a segunda podia demorar ou
     * falhar, e a tela ficava perguntando até aparecer.
     *
     * No V2 as duas acontecem na mesma transação — quando `auth/register`
     * retorna, ou o jogador existe ou não existe conta nenhuma. Não há o que
     * esperar, e esperar mostrava dois segundos de "criando seu personagem" que
     * não descreviam nada.
     */
    async startPlayerCheck() {
      const playerId = await this.$store.dispatch('auth/checkPlayerStatus');

      if (!playerId) {
        this.creatingPlayer = false;
        this.error = this.$t('login.error');
        this.$oraculoLogs.warn('Cadastro sem playerId apos register', {
          details: { email: this.formData.email, username: this.formData.username }
        });
        return;
      }

      this.playerCreated = true;
      this.loadingMessage = 'Personagem criado com sucesso!';

      this.$oraculoLogs.info('Player created successfully', {
        details: {
          email: this.formData.email,
          username: this.formData.username,
          playerId
        }
      });
    },

    async goToHome() {
      if (!temSessao()) {
        console.error('No session found');
        return;
      }

      // Limpar o intervalo antes de redirecionar
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }

      // Redirecionar para home
      await this.$router.push('/home');
    },

    async handleForgotPassword() {
      const email = this.formData.email;
      if (!email) {
        this.showAlert(this.$t('login.emailRequired'), 'error');
        return;
      }

      try {
        // Log de tentativa de reset de senha
        this.$oraculoLogs.info('Password reset requested', {
          details: {
            email
          }
        });

        await this.$store.dispatch('auth/forgotPassword', { email });
        this.error = null;
        this.showAlert(this.$t('login.resetEmailSent'), 'success');
      } catch (error) {
        // Log de erro no reset de senha
        this.$oraculoLogs.error('Password reset failed', {
          details: {
            email,
            errorMessage: error.response?.data?.error?.message
          },
          error: this.$oraculoLogs.captureErrorDetails(error)
        });

        this.showAlert(
          error.response?.data?.error?.message || this.$t('login.resetError'),
          'error'
        );
      }
    },

    showAlert(message, type = 'info') {
      this.alertConfig = { message, type };
      setTimeout(() => this.closeAlert(), 3000);
    },

    closeAlert() {
      this.alertConfig.message = '';
    }
  },

  beforeUnmount() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
}
</script>

<style scoped>
.porta {
  min-height: 100vh;
}

.porta__interior {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--e-4);
  padding: var(--e-4) var(--e-3);
  box-sizing: border-box;
}

.porta__marca {
  text-align: center;
}

.porta__logo {
  display: block;
  width: 96px;
  margin: 0 auto var(--e-2);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.45));
  animation: porta-boiar 4s ease-in-out infinite;
}

.porta__titulo {
  margin: 0;
  font-size: var(--f-5);
  letter-spacing: 2px;
}

/* As letras do título sobem e descem como a linha d'água — o repouso animado do D10-3. */
.porta__letra {
  display: inline-block;
  color: var(--ouro, #ffd76a);
  text-shadow: 0 3px 0 rgba(60, 35, 5, 0.9), 0 6px 10px rgba(0, 0, 0, 0.5);
  animation: porta-onda 2.4s ease-in-out infinite;
}

/* ------------------------------------------------------------------------------------------
 * A ficha do porto: fitas de papel em cima, a folha embaixo. As fitas usam a mesma linguagem
 * das do caderno — a ativa é papel-claro e ligada à folha; a inativa fica atrás, mais escura.
 * ---------------------------------------------------------------------------------------- */
.porta__ficha {
  width: min(420px, 100%);
}

.porta__fitas {
  display: flex;
  gap: var(--e-1);
  padding: 0 var(--e-3);
}

.porta__fita {
  border: none;
  background: var(--papel-sombra);
  color: var(--tinta-fraca);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  padding: var(--e-2) var(--e-3);
  cursor: pointer;
  transform: translateY(2px);
  transition: transform var(--t-curto) var(--curva-suave), background-color var(--t-curto) linear;
}

@media (hover: hover) {
  .porta__fita:hover {
    transform: translateY(0);
  }
}

.porta__fita--ativa {
  background: var(--papel);
  color: var(--tinta);
  transform: translateY(0);
}

.porta__papel {
  background: var(--papel);
  color: var(--tinta);
  padding: var(--e-4);
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.4);
}

.porta__erro {
  margin: 0 0 var(--e-3);
  padding: var(--e-2);
  border-left: 4px solid var(--cor-ruim);
  background: var(--papel-frio);
  color: var(--cor-ruim);
  font-size: var(--f-2);
}

.porta__form {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

.porta__campo {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

/* O mesmo papel rebaixado dos campos do jogo (loja, contribuição). */
.porta__campo input {
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
  padding: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-3);
  color: var(--tinta);
  width: 100%;
  box-sizing: border-box;
}

.porta__campo input:focus {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

.porta__senha {
  position: relative;
  display: flex;
  align-items: center;
}

.porta__olho {
  position: absolute;
  right: var(--e-1);
  border: none;
  background: none;
  color: var(--tinta-fraca);
  cursor: pointer;
  padding: var(--e-1);
}

@media (hover: hover) {
  .porta__olho:hover {
    color: var(--tinta);
  }
}

.porta__aviso {
  color: var(--cor-ruim);
  font-size: var(--f-1);
}

.porta__acoes {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-2);
  margin-top: var(--e-1);
}

.porta__esqueci {
  border: none;
  background: none;
  color: var(--tinta-fraca);
  font-size: var(--f-2);
  text-decoration: underline;
  cursor: pointer;
}

@media (hover: hover) {
  .porta__esqueci:hover {
    color: var(--tinta);
  }
}

/* A comunidade: texto discreto sobre a água, com o contraste do véu da cena. */
.porta__comunidade {
  display: flex;
  gap: var(--e-4);
}

.porta__comunidade a {
  color: var(--texto-fraco);
  font-size: var(--f-1);
  text-decoration: underline;
  text-underline-offset: 3px;
}

@media (hover: hover) {
  .porta__comunidade a:hover {
    color: var(--texto);
  }
}

@keyframes porta-boiar {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

@keyframes porta-onda {
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(-5px); }
  60% { transform: translateY(1px); }
}

@media (prefers-reduced-motion: reduce) {
  .porta__logo,
  .porta__letra {
    animation: none;
  }
}
</style>
